/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { debounce } from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';

import { IIncrementalQuantityProps } from './IncrementalQuantity.props';

/**
 * IIncrementalQuantityState interface.
 */
interface IIncrementalQuantityState {
    currentInput: number;
    isUpdating: boolean;
}

/**
 * Quantity Component - This component is used to add or remove quantity.
 */
@observer
export default class MilIncrementalQuantity extends React.PureComponent<IIncrementalQuantityProps, IIncrementalQuantityState> {
    public static defaultProps: Partial<IIncrementalQuantityProps> = {
        decrementGlyphClass: 'fas fa-minus',
        incrementGlyphClass: 'fas fa-plus'
    };

    private readonly _minValue: number;

    private readonly inputRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    /**
     * Debounced version of the onChange callback.
     * Batches rapid input changes into a single update after a 1s pause.
     *
     * @param value – The numeric quantity to propagate upstream.
     */
    private readonly debouncedOnChange = debounce((value: number) => {
        this.props.onChange && this.props.onChange(value);
    }, 1000);

    /**
     * React lifecycle hook invoked just before the component is removed from the DOM.
     * Cancels any pending debouncedOnChange calls to avoid calling onChange on an unmounted component.
     */
    public componentWillUnmount() {
        this.debouncedOnChange.cancel();
    }

    public static getDerivedStateFromProps(
        props: Readonly<IIncrementalQuantityProps>,
        state: IIncrementalQuantityState
    ): IIncrementalQuantityState {
        const nextState = { ...state };

        if (props.disabled && !state.isUpdating) {
            nextState.isUpdating = true;
        }

        if (!props.disabled && state.isUpdating) {
            nextState.currentInput = props.currentCount ?? 1;
            nextState.isUpdating = false;
        }
        return nextState;
    }

    constructor(props: IIncrementalQuantityProps) {
        super(props);
        this._onIncrement = this._onIncrement.bind(this);
        this._onDecrement = this._onDecrement.bind(this);
        this._handleChange = this._handleChange.bind(this);
        const min = this.props.min ?? MilIncrementalQuantity.defaultProps.min!;
        const multiple = this.props.multiple;
        this._minValue = min < multiple ? multiple : min;

        const maximum = this.props.max;
        let currentVal = props.currentCount ?? 1;
        if (currentVal <= this._minValue) {
            currentVal = this._minValue;
        } else if (currentVal >= maximum) {
            currentVal = maximum;
        } else if (currentVal % multiple !== 0) {
            currentVal = (Math.floor(currentVal / multiple) + 1) * multiple;
        }
        this.state = { currentInput: currentVal, isUpdating: false };
        this.props.onChange && this.props.onChange(currentVal);
    }

    public render(): JSX.Element {
        const { min, max, multiple } = this.props;

        const glyphMinusClassName: string = `${this.props.decrementGlyphClass!} quantity__controls-glyph`;
        const glyphPlusClassName: string = `${this.props.incrementGlyphClass!} quantity__controls-glyph`;
        const decrementDisabled = this.state.currentInput <= min || this.state.currentInput <= multiple || this.props.isGiftCard;
        const incrementDisabled = this.state.currentInput >= max || this.props.isGiftCard;
        const currentValue = this.state.currentInput;

        let extraClassDecrement = '';
        if (decrementDisabled) {
            // The quantity has reached its boundaries (max or min)
            extraClassDecrement = 'disabled';
        } else if (this.props.disabled) {
            // This.props.disabled shows if the state is not succeded yet
            extraClassDecrement = 'transition';
        }
        let extraClassIncrement = '';
        if (incrementDisabled) {
            // The quantity has reached its boundaries (max or min)
            extraClassIncrement = 'disabled';
        } else if (this.props.disabled) {
            // This.props.disabled shows if the state is not succeded yet
            extraClassIncrement = 'transition';
        }

        return (
            <>
                <div className='quantity' id={this.props.id}>
                    <button
                        disabled={this.props.disabled || decrementDisabled}
                        title={decrementDisabled ? '' : this.props.decrementButtonAriaLabel}
                        className={`decrement quantity__controls ${extraClassDecrement}`}
                        onClick={this._onDecrement}
                        aria-hidden={!!decrementDisabled}
                        aria-label={`${this.props.decrementButtonAriaLabel}`}
                        color='secondary'
                    >
                        <span className={glyphMinusClassName} />
                    </button>
                    <input
                        type='number'
                        className='quantity-input'
                        pattern='[0-9]*'
                        value={this.state.currentInput}
                        onChange={this._handleChange}
                        onBlur={this._validateQty}
                        aria-live='polite'
                        aria-label={`${this.props.inputQuantityAriaLabel}`}
                        role='spinbutton'
                        aria-valuemin={this._minValue}
                        aria-valuemax={max}
                        aria-valuenow={currentValue}
                        ref={this.inputRef}
                        disabled={this.props.disabled}
                        min={this._minValue}
                        max={this.props.max}
                    />
                    <button
                        disabled={this.props.disabled || incrementDisabled}
                        title={incrementDisabled ? '' : this.props.incrementButtonAriaLabel}
                        className={`increment quantity__controls ${extraClassIncrement}`}
                        onClick={this._onIncrement}
                        aria-hidden={!!incrementDisabled}
                        aria-label={`${this.props.incrementButtonAriaLabel}`}
                        color='secondary'
                    >
                        <span className={glyphPlusClassName} />
                    </button>
                </div>
            </>
        );
    }

    private _onIncrement(): void {
        let invokeCallback = false;
        const currQuantity = this.state.currentInput;
        let updatedQuantity: number;

        if (currQuantity < this.props.max) {
            invokeCallback = true;
            updatedQuantity = currQuantity + this.props.multiple;
        } else {
            invokeCallback = false;
            updatedQuantity = this.props.max;
        }

        if (invokeCallback && this.props.onChange) {
            if (this.props.onChange(updatedQuantity)) {
                this.setState({ currentInput: updatedQuantity });
            }
        }
    }

    private _onDecrement(): void {
        let invokeCallback = false;
        const currQuantity = this.state.currentInput;
        let updatedQuantity: number;

        if (currQuantity >= this._minValue) {
            invokeCallback = true;
            updatedQuantity = currQuantity - this.props.multiple;
        } else {
            invokeCallback = false;
            updatedQuantity = this._minValue;
        }

        if (invokeCallback && this.props.onChange) {
            if (this.props.onChange(updatedQuantity)) {
                this.setState({ currentInput: updatedQuantity });
            }
        }
    }

    private _handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
        const raw = e.target.value;
        // If user has completely cleared the field, or left only zeros,
        // let them finish typing before we clamp/notify.
        if (raw === '' || /^0+$/.test(raw)) {
            this.setState({ currentInput: 0 });
            return;
        }
        const currentValue = Number.parseInt(raw, 10);
        if (Number.isNaN(currentValue)) {
            // still allow editing non‑numeric, e.g. "-" or ""
            this.setState({ currentInput: NaN });
            return;
        }
        this.setState({ currentInput: currentValue });
        this.debouncedOnChange(currentValue);
    }

    /**
     * Validates user input value
     */
    private readonly _validateQty = (): void => {
        let currentValue = this.state.currentInput;

        if (Number.isNaN(currentValue)) {
            currentValue = this._minValue;
        } else {
            const multiple = this.props.multiple;
            const max = this.props.max;

            if (currentValue <= this._minValue) {
                currentValue = this._minValue;
            } else if (currentValue >= max) {
                currentValue = max;
            } else if (currentValue % multiple > 0) {
                currentValue = (Math.floor(currentValue / multiple) + 1) * multiple;
            }
        }

        if (currentValue !== this.state.currentInput) {
            this.setState({ currentInput: currentValue }, () => {
                this.props.onChange && this.props.onChange(currentValue);
            });
        }
    };
}
